'use client';
import { getCookie, setCookie } from '@dealerportal/utilities';
import { useEffect, useState } from 'react';

export interface DeploymentIdProps {
  /**
   * The initial deployment ID to set in the cookie on first render.
   * This is passed from the server and cached in the HTML
   * Can also be thought of what the browser thinks the current deployment ID is
   */
  initialDeploymentId?: string;
}
/**
 * A React component that fetches the current deployment ID from the healthcheck API endpoint,
 * stores it in a cookie, and reloads the page if the deployment ID has changed.
 *
 * Intended for cache busting and ensuring users have the latest assets after a deployment - as the healthcheck api is not cached on Cloudfront.
 */
export const DeploymentId: React.FC<DeploymentIdProps> = ({
  initialDeploymentId,
}) => {
  const [currentDeploymentId, setCurrentDeploymentId] = useState(
    initialDeploymentId || ''
  );
  useEffect(() => {
    const cookieValue = getCookie('deployment_id');
    if (cookieValue) {
      setCurrentDeploymentId(cookieValue);
    }
    if (initialDeploymentId) {
      setCookie('deployment_id', initialDeploymentId, { expires: 365 });
    }
    (async () => {
      const request = await fetch('/api/healthcheck?cacheBust=true');
      if (request.ok) {
        const data: {
          commit?: string;
        } = await request.json();
        if (!data.commit) {
          return;
        }
        setCookie('deployment_id', data.commit, { expires: 365 });
        setCurrentDeploymentId(data.commit);
      }
    })();
  }, [initialDeploymentId]);
  return <div id="deployment-id" data-deployment-id={currentDeploymentId} />;
};

export default DeploymentId;
